import { useEffect, useRef } from "react"
import { Image as ImageIcon, X } from "lucide-react"
import { DEFERRED_IMG_PROPS } from "@/lib/deferred-img"
import { cn } from "@/lib/utils"

export type ViewpointThumb = {
  id: number
  name?: string | null
  thumb_url?: string | null
  url?: string | null
}

type ViewpointThumbGridProps<T extends ViewpointThumb> = {
  viewpoints: T[]
  highlightId?: number | null
  emptyLabel: string
  labelFor: (viewpoint: T, index: number) => string
  onOpen: (viewpoint: T, index: number) => void
  /** When set, the tile click is the camera action and this opens the still. */
  onView?: (viewpoint: T, index: number) => void
  viewTitleFor?: (viewpoint: T, index: number) => string
  /** Hover and accessible name for the tile click when it restores the camera. */
  applyTitle?: string
  onRemove?: (viewpoint: T, index: number) => void
  removeTitle?: string
  /** square: three columns. wide: two 16:9 columns, same frame as scene previzes. */
  layout?: "square" | "wide"
}

/** Viewpoint tiles shared by the 3D scene media tab and the location viewpoints tab. */
export function ViewpointThumbGrid<T extends ViewpointThumb>({
  viewpoints,
  highlightId = null,
  emptyLabel,
  labelFor,
  onOpen,
  onView,
  viewTitleFor,
  applyTitle,
  onRemove,
  removeTitle,
  layout = "square",
}: ViewpointThumbGridProps<T>) {
  const highlightRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: "nearest" })
  }, [highlightId, viewpoints])

  if (viewpoints.length === 0) {
    return <p className="text-xs text-ui-text-muted">{emptyLabel}</p>
  }

  return (
    <ul className={cn("grid gap-1.5", layout === "wide" ? "grid-cols-2" : "grid-cols-3")}>
      {viewpoints.map((viewpoint, index) => {
        const label = labelFor(viewpoint, index)
        const thumb = viewpoint.thumb_url ?? viewpoint.url
        const highlighted = highlightId != null && highlightId === viewpoint.id
        return (
          <li key={viewpoint.id}>
            <div
              ref={highlighted ? highlightRef : undefined}
              className={cn(
                "relative w-full overflow-hidden rounded-lg",
                layout === "wide" ? "aspect-video" : "aspect-square",
                "border border-ui-border bg-ui-surface-sunken text-ui-text-muted",
                highlighted && "border-ui-accent ring-2 ring-ui-accent/40",
              )}
            >
              <button
                type="button"
                aria-label={applyTitle ? `${applyTitle}: ${label}` : label}
                title={applyTitle}
                className="absolute inset-0 flex items-center justify-center"
                onClick={() => onOpen(viewpoint, index)}
              >
                {thumb ? (
                  <img src={thumb} alt="" {...DEFERRED_IMG_PROPS} className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-4 w-4" aria-hidden />
                )}
              </button>
              {onView ? (
                <button
                  type="button"
                  onClick={() => onView(viewpoint, index)}
                  className={cn(
                    "absolute right-0.5 bottom-0.5 flex h-5 w-5 cursor-pointer items-center justify-center",
                    "rounded-full bg-[var(--ui-chip-bg)] text-ui-text-secondary transition-colors",
                    "hover:bg-ui-surface-raised hover:text-ui-text-primary",
                  )}
                  aria-label={viewTitleFor?.(viewpoint, index) ?? label}
                  title={viewTitleFor?.(viewpoint, index)}
                >
                  <ImageIcon className="h-3 w-3" />
                </button>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(viewpoint, index)}
                  className={cn(
                    "absolute top-0.5 right-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full",
                    "bg-[var(--ui-chip-bg)] text-ui-text-secondary transition-colors",
                    "hover:bg-red-600/80 hover:text-ui-on-accent",
                  )}
                  aria-label={removeTitle ?? label}
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
